/**
 * Labels WAITING_FOR_VERIFICATION as "re-vérification" when the same bill already has
 * an accountant-rejected payment (FAILED + rejectedAt).
 */

export function hasPriorAccountantRejectedPaymentOnBill(siblingPayments, excludePaymentId) {
  if (!Array.isArray(siblingPayments)) return false;
  return siblingPayments.some(
    (p) =>
      p &&
      p.id !== excludePaymentId &&
      p.status === 'FAILED' &&
      p.rejectedAt != null
  );
}

/** i18n key (namespace payments) for a payment row's status label. */
export function paymentStatusI18nKey(payment, siblingPaymentsOverride) {
  if (!payment?.status) return 'payments.pending';
  const status = payment.status;
  if (status === 'WAITING_FOR_VERIFICATION') {
    const siblings = siblingPaymentsOverride ?? payment?.bill?.payments;
    if (hasPriorAccountantRejectedPaymentOnBill(siblings, payment.id)) {
      return 'payments.waitingForReverification';
    }
  }
  return `payments.${String(status).toLowerCase().replace(/_/g, '')}`;
}

/** True if any payment on the bill is waiting for verification after a prior rejection on that bill. */
export function billHasReverificationWaiting(bill) {
  const siblingList = bill?.payments;
  if (!Array.isArray(siblingList)) return false;
  return siblingList.some(
    (p) =>
      p.status === 'WAITING_FOR_VERIFICATION' &&
      hasPriorAccountantRejectedPaymentOnBill(siblingList, p.id)
  );
}

const PAYMENT_CHIP_STATUSES = ['WAITING_FOR_VERIFICATION', 'FAILED', 'PENDING'];

/**
 * Payment-derived status chips for a bill row, oldest first (chronological).
 * Used in bills table status column alongside the main bill status badge.
 */
export function getBillPaymentStatusChipsSorted(bill) {
  const payments = Array.isArray(bill?.payments) ? bill.payments : [];
  const chips = [];
  for (const p of payments) {
    if (!p || !PAYMENT_CHIP_STATUSES.includes(p.status)) continue;
    let sortTime = 0;
    if (p.status === 'FAILED') {
      sortTime = new Date(p.rejectedAt || p.processedDate || 0).getTime();
    } else {
      sortTime = new Date(p.processedDate || 0).getTime();
    }
    if (!Number.isFinite(sortTime)) sortTime = 0;
    chips.push({
      key: `${p.id}-${p.status}`,
      sortTime,
      status: p.status,
      payment: p
    });
  }
  chips.sort((a, b) => {
    if (a.sortTime !== b.sortTime) return a.sortTime - b.sortTime;
    return String(a.payment.id).localeCompare(String(b.payment.id));
  });
  return chips;
}

/** Sort key for ordering payment lifecycle events on a bill (chronological). */
export function paymentTimelineSortTime(p) {
  if (!p) return 0;
  if (p.status === 'FAILED' && p.rejectedAt) {
    return new Date(p.rejectedAt).getTime();
  }
  if (p.status === 'COMPLETED' && p.approvedAt) {
    return new Date(p.approvedAt).getTime();
  }
  return new Date(p.processedDate || 0).getTime();
}

/**
 * Hide a FAILED row when the client (or staff) later recorded a non-failed payment on the same bill
 * (re-submission after rejection). Keeps one logical "current" line in the payments list.
 */
export function shouldHideSupersededFailedPaymentRow(payment, siblingPayments) {
  if (!payment || payment.status !== 'FAILED') return false;
  const siblings = siblingPayments ?? payment?.bill?.payments;
  if (!Array.isArray(siblings)) return false;
  const t0 = new Date(payment.processedDate || 0).getTime();
  return siblings.some(
    (x) =>
      x &&
      x.id !== payment.id &&
      x.status !== 'FAILED' &&
      new Date(x.processedDate || 0).getTime() > t0
  );
}

/**
 * All payments on the bill up to and including this row's lifecycle time, sorted chronologically
 * (for stacked status chips on /payments).
 */
export function getBillPaymentsTimelineChipsUpToRow(row) {
  const siblings = row?.bill?.payments;
  if (!Array.isArray(siblings) || siblings.length === 0) {
    return [{ key: row.id, payment: row }];
  }
  const rowT = paymentTimelineSortTime(row);
  const items = siblings
    .map((p) => ({
      payment: p,
      time: paymentTimelineSortTime(p)
    }))
    .filter((x) => Number.isFinite(x.time) && x.time <= rowT)
    .sort((a, b) => {
      if (a.time !== b.time) return a.time - b.time;
      return String(a.payment.id).localeCompare(String(b.payment.id));
    });
  return items.map((x) => ({ key: x.payment.id, payment: x.payment }));
}
